import { CommonModule } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmDialogService } from '@serviceplan/ui-kit';
import {
  AssetUIModel,
  AssetsGenerateUrlService,
  ClientCoreModule,
  ClientEnvironment,
  CommonUtilityLib,
  ENVIRONMENT,
  SpDateFormatPipe,
  TableColumn,
  ValueListsService,
  anyAsset,
} from '@thehubz/client/core';
import { Asset, AssetsApiService, MakeBuyoutRights, UpdateAssetManyDto, UsageRights } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, BuyoutRights, BuyoutRightsMakeOS, BuyoutRightsMakeOSSecondLevel, BuyoutRightsSeat, MULTI_VALUE } from '@thehubz/shared/model';
import { Observable, filter, firstValueFrom, map, of, switchMap } from 'rxjs';
import { AssetCommonService } from '../../../../services/asset-common.service';
import { AssetUploadService } from '../../../../services/asset-upload.service';
import { AssetNotesDialogComponent } from '../../../asset-buyout-rights/asset-notes-dialog/asset-notes-dialog.component';
import { BuyoutUtilsService } from '../../../asset-buyout-rights/buyout-utils.service';
import { getBuyoutRightsConfig } from '../../../asset-upload/stepper-config/asset-settings-stepper.constants';
import { AssetsPageService } from '../../../assets/assets-page/assets-page.service';
import { BuyoutRightsColumnData } from '../table-column-data';

@Component({
  selector: 'thehubz-asset-details-buyout-rights',
  standalone: true,
  templateUrl: './asset-details-buyout-rights.component.html',
  styleUrls: ['./asset-details-buyout-rights.component.scss'],
  imports: [CommonModule, MatIconModule, MatTooltipModule, MatExpansionModule, ClientCoreModule, MatCheckboxModule],
})
export class AssetDetailsBuyoutRightsComponent implements OnInit, OnDestroy {
  @Input() uploadedAssetLists?: AssetUIModel[];
  assetIds?: string[];
  usageRightIds?: string[];
  brandingKey!: CustomClients;
  CustomClients = CustomClients;
  /** BuyoutRights to be used with optionsLabel */
  buyoutRights = BuyoutRights;
  /** BuyoutRights MakeOS to be used with optionsLabel */
  buyoutRightsMakeOS = BuyoutRightsMakeOS;
  /** BuyoutRights MakeOS second level to be used with optionsLabel */
  buyoutRightsMakeOSSecondLevel = BuyoutRightsMakeOSSecondLevel;
  /** Buyout rights configuration JSON */
  buyoutRightsConfigs!: AnyType;
  /** Table column configuration JSON  */
  columns: TableColumn[] = [];

  notesColumnName = '';

  dialogRef!: MatDialogRef<AssetNotesDialogComponent>;

  AnyAsset = anyAsset;

  /** Observable to get asset usage rights and buyouts  */
  assetUsageRights$: Observable<UsageRights[] | Record<string, AnyType>[] | undefined> = this.assetCommonService.assetDataSubject$.pipe(
    filter((asset) => !this.commonUtilityLib.isEmptyObject(asset)),
    map((asset) => asset?.usageRights),
  );

  /** Observable to get asset usage rights and buyouts  */
  assetMakeOSBuyoutRights$: Observable<MakeBuyoutRights[] | Record<string, AnyType>[] | undefined> = this.assetCommonService.assetDataSubject$.pipe(
    filter((asset) => !this.commonUtilityLib.isEmptyObject(asset)),
    map((asset) => {
      return asset?.makeBuyoutRights;
    }),
  );

  /** Observable to get asset notes  */
  assetNotes$: Observable<string | undefined> = this.assetCommonService.assetDataSubject$.pipe(
    filter((asset) => !this.commonUtilityLib.isEmptyObject(asset)),
    map((asset) => asset?.notes),
  );

  /** Observable to get asset owner and expiry date  */
  assetInfo$: Observable<Partial<AssetUIModel> | undefined> = this.assetCommonService.assetDataSubject$.pipe(
    filter((asset) => !this.commonUtilityLib.isEmptyObject(asset)),
    map((asset) => ({ owner: asset?.owner, expiryDate: asset?.expiryDate ? this.spDatePipe.transform(asset?.expiryDate) : '' })),
    switchMap((value) => {
      return this.valueListsService.lookupUserInfo(value.owner)?.pipe(map((owner) => ({ ['Owner']: owner.name, ['Expiry date']: value.expiryDate }))) || of({});
    }),
  );

  /** Observable to get asset usage rights and buyouts  */
  unMatchedUsageRight$: Observable<string[]> = this.assetCommonService.unMatchedUsageRight$;

  expandedPanel$: Observable<number> = this.assetCommonService.openUpdatedPanel$;

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    private dialog: MatDialog,
    private spDatePipe: SpDateFormatPipe,
    private assetsService: AssetsApiService,
    private confirmDialog: ConfirmDialogService,
    private commonUtilityLib: CommonUtilityLib,
    private assetsPageService: AssetsPageService,
    private valueListsService: ValueListsService,
    private assetCommonService: AssetCommonService,
    private assetUploadService: AssetUploadService,
    protected buyoutUtilsService: BuyoutUtilsService,
    private assetUrlService: AssetsGenerateUrlService,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
  }

  ngOnInit() {
    if (this.brandingKey === CustomClients.SEAT) {
      this.notesColumnName = 'Zusatzinformationen';
      this.buyoutRights = BuyoutRightsSeat;
    } else if (this.brandingKey === CustomClients.Standard) {
      this.columns = BuyoutRightsColumnData.TABLE_MAKEOS_COLUMNS;
    } else {
      this.columns = BuyoutRightsColumnData.TABLE_DEFAULT_COLUMNS;
    }
    this.buyoutRightsConfigs = getBuyoutRightsConfig(this.brandingKey);
    if (this.uploadedAssetLists) {
      this.assetIds = this.uploadedAssetLists.map((list) => list._id);
    }
  }

  /**
   * Use with keyvalue pipe to disable automatic keyvalue sorting
   * @returns
   */
  originalOrder = (): number => {
    return 0;
  };

  async editBuyoutRights(type: string, index: number, usageRightId?: string) {
    const asset = await firstValueFrom(this.assetCommonService.assetDataSubject$);
    const uploadedAssets = this.uploadedAssetLists?.length ? this.uploadedAssetLists : [asset as AssetUIModel];
    this.assetsPageService.editDialogConfirmation(uploadedAssets).subscribe((data: AnyType) => {
      if (!data) return;
      switch (type) {
        case 'editNotes':
          this.dialogRef = this.dialog.open<AssetNotesDialogComponent, { notes: string }>(AssetNotesDialogComponent, {
            maxWidth: '40rem',
            width: '50%',
            height: '52%',
            data: { notes: asset?.notes || '' },
          });
          this.dialogRef.disableClose = true;
          this.dialogRef.afterClosed().subscribe(async (notes: string) => {
            if (notes === undefined || notes === null) return;
            const assetStatus = this.brandingKey === CustomClients.Vorwerk ? Asset.StatusEnum.AwaitingApproval : undefined;
            // called from asset upload stepper => usage-right/buyout-rights dialog
            if (this.uploadedAssetLists?.length && this.assetIds?.length) {
              const updateAssetManyDto: UpdateAssetManyDto = {
                ids: this.assetIds,
                updateData: { notes, status: assetStatus },
              };
              const assets = await this.assetUploadService.updateManyAsset(updateAssetManyDto);
              // the new asset contains current buyoutRights, to make align with the modified uploaded asset, reassign the buyout rights again
              if (this.brandingKey === CustomClients.Standard) assets[0].makeBuyoutRights = this.uploadedAssetLists[0].makeBuyoutRights || [];
              else assets[0].usageRights = this.uploadedAssetLists[0].usageRights || [];
              this.emitUpdatedAsset(assets[0]);
            } else {
              // called directly from buyout rights tab for individual asset update
              this.assetsService.assetsControllerUpdate(asset?._id || '', { notes, status: assetStatus }).subscribe(this.emitUpdatedAsset);
            }
          });
          break;
        case 'editBuyouts': {
          const singleAssetUpdate = !this.uploadedAssetLists || this.uploadedAssetLists?.length === 1 ? true : false;
          if (data) {
            this.buyoutUtilsService
              .assetBuyoutRightsDialogs(uploadedAssets, false, singleAssetUpdate, true, index, usageRightId)
              .subscribe((update) => this.handleAssetUpdate(update, uploadedAssets[0], usageRightId || ''));
          }
          break;
        }
      }
    });
  }

  private handleAssetUpdate(update: { updatedAsset: AssetUIModel; updatedProperties?: string[] }, originalAsset: AssetUIModel, usageRightId: string) {
    const updateAsset = update.updatedAsset;
    const updatedProperties = update.updatedProperties || [];
    const oldAssetUsageRights = this.findUsageRights(originalAsset, usageRightId);
    if (oldAssetUsageRights) {
      const updatedAssetUsageRights = this.findUsageRights(updateAsset, usageRightId);
      const hasMultipleValues = this.hasMultipleValues(oldAssetUsageRights);

      if (hasMultipleValues) {
        this.updateOldUsageRights(oldAssetUsageRights, updatedAssetUsageRights, updatedProperties);
        this.assetCommonService.assetDataSubject$.next(originalAsset);
      } else {
        this.assetCommonService.assetDataSubject$.next(updateAsset);
      }
    }
  }

  private hasMultipleValues(usageRights: AnyType): boolean {
    return Object.values(usageRights).some((right) => right === MULTI_VALUE);
  }

  private findUsageRights(asset: AssetUIModel, usageRightId: string): AnyType {
    return asset.usageRights?.find((right) => right._id === usageRightId) as AnyType;
  }

  private updateOldUsageRights(oldRights: AnyType, newRights: AnyType, properties: string[]) {
    properties.forEach((prop) => {
      oldRights[prop] = newRights[prop];
    });
  }

  /**
   * Delete buyout rights
   * @param usageRight
   */
  deleteBuyoutRights(usageRight: { _id: string; name: string }) {
    this.confirmDialog
      .open({
        title: $localize`Delete <b>Buyout rights: ${usageRight.name}</b>`,
        message: $localize`It will NOT be possible to recover the information after deleting.`,
        confirmCaption: $localize`:dialog button:Delete`,
        confirmCaptionIcon: 'delete',
        confirmCaptionCssStyle: '!justify-start',
      })
      .subscribe(async (confirmed) => {
        if (!confirmed) return;

        const asset = (await firstValueFrom(this.assetCommonService.assetDataSubject$)) as AssetUIModel;

        if (!this.uploadedAssetLists?.length || !this.assetIds?.length) {
          this.deleteUsageRightForIndividualAsset(asset, usageRight._id);
          return;
        }

        const firstAsset = this.uploadedAssetLists[0];

        if (firstAsset.usageRights?.length) {
          this.deleteBuyoutRightsFromUploadedAssets(firstAsset, usageRight._id);
          return;
        }

        if (firstAsset.makeBuyoutRights?.length && Array.isArray(usageRight._id)) {
          this.deleteMakeOSUsageRightFromUploadedAssets(firstAsset, usageRight._id);
        }
      });
  }

  private deleteUsageRightForIndividualAsset(asset: Asset, usageRightId: string): void {
    const assetId = asset._id;

    const deleteObservable =
      this.brandingKey === CustomClients.Standard
        ? this.assetsService.assetsControllerDeleteMakeOSUsageRight(assetId, usageRightId)
        : this.assetsService.assetsControllerDeleteUsageRight(assetId, usageRightId);

    deleteObservable.subscribe(this.emitUpdatedAsset);
  }

  private deleteBuyoutRightsFromUploadedAssets(firstAsset: AssetUIModel, usageRightId: string): void {
    const uploadedAssetUsageRights = firstAsset.usageRights;

    const allUsageRights = this.uploadedAssetLists?.flatMap((list) => list?.usageRights ?? []) as UsageRights[];
    const targetBuyoutRightsTitle = allUsageRights.find((ur) => ur._id.toString() === usageRightId)?.buyoutRightsTitle;

    if (!targetBuyoutRightsTitle) return;

    this.usageRightIds = allUsageRights.filter((ur) => ur.buyoutRightsTitle === targetBuyoutRightsTitle).map((ur) => ur._id.toString());

    this.assetsService
      .assetsControllerDeleteManyUsageRight(this.assetIds || [], this.usageRightIds)
      .subscribe(() => this.updateDeletedBuyoutRights(uploadedAssetUsageRights || [], usageRightId));
  }

  private deleteMakeOSUsageRightFromUploadedAssets(firstAsset: AssetUIModel, usageRightId: string[]): void {
    const uploadedAssetUsageRights = firstAsset.makeBuyoutRights;
    this.assetsService
      .assetsControllerDeleteManyMakeOSUsageRight(this.assetIds || [], usageRightId)
      .subscribe(() => this.updateDeletedMakeBuyoutRights(uploadedAssetUsageRights || [], usageRightId[0]));
  }

  updateDeletedBuyoutRights(uploadedAssetUsageRights: UsageRights[], usageRightId: string) {
    this.removeRightById(uploadedAssetUsageRights, usageRightId);
  }

  updateDeletedMakeBuyoutRights(uploadedAssetUsageRights: MakeBuyoutRights[], usageRightId: string) {
    this.removeRightById(uploadedAssetUsageRights, usageRightId);
  }

  private removeRightById<T extends { _id: string }>(rightsList: T[], deletedId: string): void {
    const index = rightsList.findIndex((right) => (Array.isArray(right._id) ? right._id.includes(deletedId) : right._id === deletedId));
    if (index !== -1) rightsList.splice(index, 1);
    this.emitUpdatedAsset(this.uploadedAssetLists?.[0] as Asset);
  }

  emitUpdatedAsset = (asset: Asset) => {
    const responseData = this.assetUrlService.generatePreview([asset]);
    this.assetCommonService.assetDataSubject$.next(responseData[0]);
  };

  ngOnDestroy(): void {
    this.assetCommonService.openUpdatedPanel$.next(0);
  }
}
